import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PageWrapper from '../components/PageWrapper';
import MarketingSection from '../components/marketing/MarketingSection';
import FadeIn from '../components/marketing/FadeIn';
import CategoryTag from '../components/marketing/CategoryTag';
import { blogPosts } from '../data/blogPosts';

export default function ArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find((p) => p.slug === slug);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    if (!post) {
      navigate('/blog', { replace: true });
    }
  }, [post, navigate]);

  useEffect(() => {
    function handleScroll() {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0;
      setScrollProgress(progress);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!post) return null;

  const initials = post.author.split(' ').map((w) => w[0]).join('');

  return (
    <PageWrapper>
      <div
        className="article-progress-bar"
        style={{ width: `${scrollProgress}%` }}
        aria-hidden="true"
      />

      <section className="marketing-hero marketing-hero--article">
        <div className="max-w-3xl mx-auto px-8 lg:px-12">
          <FadeIn delay={0.05}>
            <Link to="/blog" className="article-back-link">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M12 7H2M5 4L2 7l3 3" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back to Blog
            </Link>
          </FadeIn>

          <FadeIn delay={0.1}>
            <CategoryTag label={post.category} />
          </FadeIn>

          <FadeIn delay={0.18}>
            <h1
              className="landing-heading"
              style={{
                fontSize: 'clamp(2.25rem, 4.5vw, 3.75rem)',
                lineHeight: 1.1,
                letterSpacing: '-0.035em',
                color: 'var(--text-primary)',
                margin: '1.75rem 0 2rem',
              }}
            >
              {post.title}
            </h1>
          </FadeIn>

          <FadeIn delay={0.26}>
            <div className="article-meta-row">
              <div className="featured-article__author-avatar" style={{ width: 40, height: 40, fontSize: '1rem' }}>
                {initials}
              </div>
              <div>
                <div className="featured-article__author-name">{post.author}</div>
                <div className="featured-article__author-meta">{post.authorTitle}</div>
              </div>
              <div className="featured-article__author-meta" style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div>{post.date}</div>
                <div>{post.readTime}</div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <MarketingSection padding="md" maxWidth="3xl">
        <div className="marketing-prose">
          {post.body && post.body.length > 0 ? (
            post.body.map((block, i) => {
              if (block.type === 'paragraph') {
                return (
                  <FadeIn key={i} delay={0.05}>
                    <p className="marketing-prose__p" style={{ fontSize: '1.0625rem' }}>
                      {block.text}
                    </p>
                  </FadeIn>
                );
              }
              if (block.type === 'pullquote') {
                return (
                  <FadeIn key={i} delay={0.05}>
                    <blockquote>{block.text}</blockquote>
                  </FadeIn>
                );
              }
              if (block.type === 'heading') {
                return (
                  <FadeIn key={i} delay={0.05}>
                    <h2
                      className="landing-heading"
                      style={{
                        fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
                        lineHeight: 1.25,
                        margin: '3rem 0 1.25rem',
                      }}
                    >
                      {block.text}
                    </h2>
                  </FadeIn>
                );
              }
              return null;
            })
          ) : (
            <FadeIn delay={0.1}>
              <p className="marketing-prose__p" style={{ fontStyle: 'italic' }}>
                This article is coming soon. Check back shortly for the full piece.
              </p>
            </FadeIn>
          )}
        </div>

        <FadeIn delay={0.1}>
          <div className="article-footer-actions">
            <Link to="/blog" className="btn-secondary">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M12 7H2M5 4L2 7l3 3" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              All Articles
            </Link>
            <Link to="/schedule-demo" className="landing-cta landing-cta--hero">
              Get Free Risk Report
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M2 6h8M7 3l3 3-3 3" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>
        </FadeIn>
      </MarketingSection>
    </PageWrapper>
  );
}
